import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import { z } from 'zod'
import { PartnerTier } from './types'
import { DateTimeSchema } from '../common'

extendZodWithOpenApi(z)

export const TenantType = z.enum([
  'LykaStaff',
  'Partner',
  'FulfillmentStaff',
  'D2cCustomer',
  'Guest',
  'TygaCustomer',
  'TygaLead',
  'LykaLead',
  'CustomerCare',
  // Partners
  'Breeder',
  'Vet',
  'Influencer',
  'OtherPartnerBusiness',
  'Affiliate',
  'Research',
  'Rescue',
  'BehaviouristTrainer',
])
export type TenantType = z.infer<typeof TenantType>

export const HowDidYouHearAboutUs = z.object({
  id: z.string().uuid(),
  primaryReason: z.string().min(1),
  secondaryReason: z.string().min(1).optional(),
  otherReason: z.string().min(1).optional(),
}).strict()
export type HowDidYouHearAboutUs = z.infer<typeof HowDidYouHearAboutUs>

/** Shape of the `users.metadata` JSONB column in the auth service. */
export const UserMetadata = z.object({
  oktaUserId: z.string().min(1).optional(),
  howDidYouHearAboutUs: HowDidYouHearAboutUs.optional(),
}).strict()
export type UserMetadata = z.infer<typeof UserMetadata>

/**
 * `metadata` accepted by the migration endpoints. Persisted to
 * `user_id_mapping.legacy_user_id`, not to `users.metadata`.
 */
export const MigrationUserMetadata = z.object({
  legacyUserId: z.number().int().positive(),
}).strict()
export type MigrationUserMetadata = z.infer<typeof MigrationUserMetadata>

export const GetUsersResponse = z.object({
  result: z.array(
    z.object({
      cognito_user_id: z.string().uuid(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.string(),
      phone_number: z.string(),
      role_name: z.string(),
      tenant_type: TenantType,
      metadata: UserMetadata.optional(),
    }),
  ),
})
export type GetUsersResponse = z.infer<typeof GetUsersResponse>

export const BusinessProfile = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  businessName: z.string().nullable(),
  abn: z.string().regex(/^\d{11}$/).nullable(),
  tier: z.nativeEnum(PartnerTier).nullable(),
  discountId: z.string().uuid(),
  hubspotCompanyId: z.string().nullable(),
  deactivatedAt: DateTimeSchema.nullable(),
  deactivatedBy: z.string().uuid().nullable(),
  createdBy: z.string().uuid().nullable(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  deletedAt: DateTimeSchema.nullable(),
})
export type BusinessProfile = z.infer<typeof BusinessProfile>

export const CreateBusinessProfile = BusinessProfile.omit({
  id: true,
  createdBy: true,
  deactivatedAt: true,
  deactivatedBy: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export type CreateBusinessProfile = z.infer<typeof CreateBusinessProfile>

export const UpdateBusinessProfile = BusinessProfile.pick({
  businessName: true,
  abn: true,
  tier: true,
  discountId: true,
  hubspotCompanyId: true,
}).partial()
export type UpdateBusinessProfile = z.infer<typeof UpdateBusinessProfile>

export const GenericBusinessProfileParam = z.object({
  userId: z.string().uuid(),
})
export type GenericBusinessProfileParam = z.infer<typeof GenericBusinessProfileParam>
